import { sample } from 'effector';
import { isAndroid } from 'libs/is-android';

import { navigate } from 'libs/router';
import { MountType } from 'model/domain';
import { log } from 'model/helpers/log';
import { mountType$ } from 'model/main.model';
import { RootDomain } from 'model/root.model';
import { routes as verifyRoutes } from 'features/verify/routes';
import { routes as fallbackRoutes } from 'features/fallback/routes';

export function mapErrorName(name: string, isDeclinedScreen = false) {
	switch (name) {
		case 'NotAllowedError':
			return isAndroid() || isDeclinedScreen
				? ErrorType.camera_access_denied
				: ErrorType.camera_access_declined;
		case 'OverconstrainedError':
			return ErrorType.poor_camera_quality;
		default:
			return ErrorType.camera_error;
	}
}

export const photoErrorsMap = {
	'session_invalid': 'session.err',
	'document_not_supported': 'document.err',
	'image_blurry': 'image.err.quality.blur',
	'image_glaring': 'image.err.quality.glare',
	'image_dark': 'image.err.quality.dark',
	'image_bright': 'image.err.quality.bright',
	'document_missing': 'image.err.doc.missing',
	'document_side_mismatch': 'image.err.doc.wrong_side',
	'document_damaged': 'image.err.doc.damaged',
	'document_cropped': 'image.err.doc.visible',
	'document_cutoff': 'image.err.doc.edge',
	'document_monochrome': 'image.err.doc.color',
	'face_missing': 'image.err.face.missing',
	'multiple_faces': 'image.err.face.multiple',
	'sunglasses_not_allowed': 'image.err.face.sunglasses',
	'face_too_large': 'image.err.face.large',
	'face_too_small': 'image.err.face.small',
	'face_cropped': 'image.err.face.visible',
	'face_not_straight': 'image.err.face.angle',
};

export enum ErrorType {
	unhandled = 'unhandled',
	invalid_device = 'invalid_device',
	invalid_session = 'invalid_session',
	camera_access_declined = 'camera_access_declined',
	poor_camera_quality = 'poor_camera_quality',
	camera_access_denied = 'camera_access_denied',
	camera_error = 'camera_error',
}

export const errorType$ = RootDomain.store<ErrorType>(null);
export const setError = RootDomain.event<ErrorType>();
export const clearError = RootDomain.event();

errorType$
	.on(setError, (_, errorType) => errorType)
	.on(clearError, () => null);

sample({
	source: { mountType: mountType$ },
	clock: setError,
	fn: ({ mountType }, errorType) => {
		switch (errorType) {
			case ErrorType.invalid_session:
				return mountType === MountType.fallback ?
					fallbackRoutes.error.invalidSession :
					verifyRoutes.error.invalidSession;
			case ErrorType.unhandled:
				return mountType === MountType.fallback ?
					fallbackRoutes.error.unhandled :
					verifyRoutes.error.unhandled;
			case ErrorType.invalid_device:
				return fallbackRoutes.error.invalidDevice;
			case ErrorType.camera_access_declined:
				return verifyRoutes.error.cameraAccessDeclined;
			case ErrorType.camera_access_denied:
				return verifyRoutes.error.cameraAccessDenied;
			case ErrorType.camera_error:
				return verifyRoutes.error.cameraError;
			case ErrorType.poor_camera_quality:
				return verifyRoutes.error.poorCameraQuality;
		}
	},
	target: navigate,
});

errorType$.watch(error => {
	log({ name: 'error_type', message: { error_type: error } });
});
